import type { PartyContact } from '../components/common/usePartyContact';
import { date as fmtDate, money } from './format';

/**
 * Builds `wa.me` and `sms:` links so a message can be sent from the admin's own WhatsApp or SMS
 * app.
 *
 * Deliberately requires no Meta account, no access token and no approved templates: these are
 * ordinary links that open the installed app with the text already typed in. The trade-off is that
 * they are NOT automatic — a person still presses Send — which is exactly what lets them work with
 * zero setup and zero cost. The automatic server-side path (IAccountNotifier on the backend) stays
 * as it is; this sits alongside it for use while Meta approval is pending, or instead of it.
 */

const NATIONAL_SIGNIFICANT_LENGTH = 10; // Pakistani mobile without the trunk 0

/**
 * Digits-only E.164 without the '+', mirroring the backend's PhoneNumberFormatter. Returns null
 * when the input cannot be read confidently, rather than guessing — the same rule the server uses,
 * so we never open a chat with a number we effectively invented.
 */
export function normalizePhone(raw: string | null | undefined, countryCode = '92'): string | null {
  if (!raw) return null;

  let digits = raw.replace(/\D/g, '');
  if (!digits) return null;

  // International prefix written as 00 (e.g. 0092300...)
  if (digits.startsWith('00')) digits = digits.slice(2);

  // Already fully qualified: 92 + 10 digits
  if (digits.startsWith(countryCode) && digits.length === countryCode.length + NATIONAL_SIGNIFICANT_LENGTH) {
    return digits;
  }

  // Local form with trunk prefix: 0300-1234567
  digits = digits.replace(/^0+/, '');
  if (digits.length === NATIONAL_SIGNIFICANT_LENGTH) return countryCode + digits;

  return null;
}

/** Opens WhatsApp with the message pre-filled. Without a number it opens the contact picker. */
export function whatsAppUrl(phone: string | null, message: string): string {
  const text = encodeURIComponent(message);
  return phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
}

/** Opens the device's SMS app with the message pre-filled. iOS wants '&body=', everything else '?body='. */
export function smsUrl(phone: string, message: string): string {
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  return `sms:+${phone}${isIos ? '&' : '?'}body=${encodeURIComponent(message)}`;
}

/** Where the recipient logs in to see the full record — derived from wherever this app is served. */
export const loginLink = () => `${window.location.origin}/login`;

// ---------- message templates ----------
//
// Plain text, composed here rather than on the server: these go out through the admin's own apps,
// so there is no Meta template to match and the admin can edit any of it before sending.

/** Signature block closing every official message. */
const COMPANY = [
  'Moiz Mobile & Corporation',
  'Danwran (Lodhran)',
  'Rana Asif Ali',
  '0300-8694092 | 0300-7194095',
].join('\n');

/** Drops any line whose value is missing, so a blank CNIC never prints as "CNIC: —". */
function lines(...rows: (string | null | false | undefined)[]): string[] {
  return rows.filter((row): row is string => !!row);
}

const dealTypeLabel = (dealType: string) =>
  dealType === 'FixedReturn' ? 'Fixed return' : 'Monthly % based';

/**
 * The account block. Deliberately just the identifiers and the as-of date: percentage profit
 * accrues daily, so a position quoted without a date stops being true the next morning.
 */
function accountBlock(c: PartyContact, asOf: string, showType = true): string[] {
  return lines(
    '*ACCOUNT*',
    showType && `Account type: ${dealTypeLabel(c.dealType)}`,
    `As of: ${fmtDate(asOf)}`,
  );
}

/**
 * The position, one variant per (direction × model). The shape is the same throughout — capital,
 * what is owed, what has moved, what is left — so only the verb changes with direction:
 * paid/payable for money taken IN from an investor, received/receivable for money placed OUT.
 *
 * A fixed account quotes one capital figure, since nothing is drawn down before maturity. A
 * percentage account quotes two: gross capital paid in, then what is left after withdrawals.
 * The last line always equals the one two above minus the one above it; keeping that true is
 * what makes the message checkable.
 */
function positionBlock(c: PartyContact): string[] {
  const isOut = c.partyType === 'Dealer';

  if (c.dealType === 'FixedReturn') {
    // The agreed sum coming back is capital plus the fixed profit on it.
    const payable = c.currentPrincipal + c.outstandingProfit;
    return lines(
      `Total investment: PKR ${money(c.currentPrincipal)}`,
      `Total ${isOut ? 'receivable' : 'payable'} with profit: PKR ${money(payable)}`,
      `Total ${isOut ? 'received' : 'paid'} (to date): PKR ${money(c.paidProfit)}`,
      c.fixedRemaining !== null &&
        `Remaining ${isOut ? 'receivable' : 'payable'}: PKR ${money(c.fixedRemaining)}`,
    );
  }

  // Percentage: profit earned to date is what has been settled plus what is still outstanding.
  const totalProfit = c.paidProfit + c.outstandingProfit;
  return lines(
    `Total investment: PKR ${money(c.totalDeposited)}`,
    `Total investment remaining: PKR ${money(c.currentPrincipal)}`,
    `Total profit (to date): PKR ${money(totalProfit)}`,
    `Profit ${isOut ? 'received' : 'paid'} (to date): PKR ${money(c.paidProfit)}`,
    `Profit remaining: PKR ${money(c.outstandingProfit)}`,
  );
}

/**
 * The position with no profit lines, for messages whose subject is the money moved rather than
 * what it has earned. The last line always equals the one two above minus the one above it.
 *
 * A fixed account states its lines against the agreed return, because capital and its uplift come
 * back from a single pot: subtracting withdrawals from capital alone would not reconcile.
 */
function capitalBlock(c: PartyContact): string[] {
  const isOut = c.partyType === 'Dealer';

  if (c.dealType === 'FixedReturn') {
    return lines(
      `Total investment: PKR ${money(c.currentPrincipal)}`,
      `Total return agreed: PKR ${money(c.fixedReturnAgreed ?? 0)}`,
      `Total ${isOut ? 'received' : 'withdrawn'}: PKR ${money(c.totalWithdrawn)}`,
      `Total remaining: PKR ${money(c.fixedRemaining ?? 0)}`,
    );
  }

  return lines(
    `Total investment: PKR ${money(c.totalDeposited)}`,
    `Investment ${isOut ? 'returned' : 'withdrawn'}: PKR ${money(c.totalWithdrawn)}`,
    `Total investment remaining: PKR ${money(c.currentPrincipal)}`,
  );
}

/** Account statement — the standing position, in the format agreed for all four account kinds. */
export function accountOpenedMessage(c: PartyContact): string {
  return [
    `Assalam-o-Alaikum ${c.name},`,
    '',
    'Your investment account statement is below.',
    '',
    '*ACCOUNT*',
    `Account type: ${dealTypeLabel(c.dealType)}`,
    '',
    '*POSITION*',
    `Total investment: PKR ${money(c.currentPrincipal)}`,
    `Please login to see more details: ${loginLink()}`,
    '',
    'Your password has been shared on your WhatsApp number. Thanks',
    '',
    COMPANY,
  ].join('\n');
}

export interface TransactionMessageOptions {
  /** 'capital' drops the profit lines, for events whose subject is the money placed. */
  position?: 'full' | 'capital';
  /** False hides the deal-type line, where naming the model adds nothing for the recipient. */
  showAccountType?: boolean;
}

/** Transaction notification — the POSITION block, stated as at the transaction date. */
export function transactionMessage(
  c: PartyContact,
  eventLabel: string,
  amount: number,
  when: string,
  { position = 'full', showAccountType = true }: TransactionMessageOptions = {},
): string {
  return [
    `Assalam-o-Alaikum ${c.name},`,
    '',
    'A transaction has been recorded on your account.',
    '',
    '*TRANSACTION*',
    `Type: ${eventLabel}`,
    `Amount: PKR ${money(amount)}`,
    `Date: ${fmtDate(when)}`,
    '',
    ...accountBlock(c, when, showAccountType),
    '',
    '*POSITION AFTER THIS TRANSACTION*',
    ...(position === 'capital' ? capitalBlock(c) : positionBlock(c)),
    '',
    `Please login to see more details: ${loginLink()}`,
    '',
    'Please contact us if this does not look correct.',
    '',
    COMPANY,
  ].join('\n');
}

/**
 * Builds a PartyContact for an account that has just been created, so the opening message can be
 * sent immediately without waiting for the ledger endpoints to catch up.
 *
 * A brand-new account has a known position: the opening amount is the capital, nothing has been
 * paid or received yet, and for a fixed account the agreed profit is the difference between the
 * return and the amount. Deriving it here rather than re-fetching avoids sending a message with
 * zeros in it because a query had not resolved.
 */
export function newAccountContact(input: {
  partyType: PartyContact['partyType'];
  name: string;
  fatherName?: string | null;
  phone?: string | null;
  cnic?: string | null;
  code: string;
  dealType: PartyContact['dealType'];
  /** The opening amount. Zero when the account was created without one. */
  initialAmount: number;
  /** Fixed-return accounts only: the whole sum agreed to come back, capital included. */
  returnAmount?: number | null;
  openedOn: string;
}): PartyContact {
  const isFixed = input.dealType === 'FixedReturn';
  const agreedProfit = isFixed ? Math.max(0, (input.returnAmount ?? 0) - input.initialAmount) : 0;

  return {
    partyType: input.partyType,
    name: input.name,
    fatherName: input.fatherName ?? null,
    phone: input.phone ?? null,
    cnic: input.cnic ?? null,
    address: null,
    code: input.code,
    dealType: input.dealType,
    status: 'Active',
    memberSince: input.openedOn,

    // Nothing has been drawn on day one, so gross and placed capital are the same figure.
    totalDeposited: input.initialAmount,
    totalWithdrawn: 0,
    fixedReturnAgreed: isFixed ? input.returnAmount ?? 0 : null,
    currentPrincipal: input.initialAmount,
    // Nothing has accrued or been settled on day one.
    outstandingProfit: agreedProfit,
    paidProfit: 0,
    fixedRemaining: isFixed ? input.returnAmount ?? 0 : null,

    ready: true,
  };
}

/**
 * Sends the opening statement without anyone pressing a button in this app.
 *
 * Two paths, in order of preference:
 *
 * 1. The server already sent it. When WhatsApp is configured on the backend, `IAccountNotifier`
 *    fires WhatsApp and SMS by itself the moment the account commits — nothing needs to happen
 *    here, and this returns immediately.
 *
 * 2. The server's sender is switched off. WhatsApp is then opened automatically with the number
 *    and message already filled in, so the admin does not hunt for a button. A browser cannot
 *    dispatch a WhatsApp message silently — no web app can — so the final Send still happens in
 *    WhatsApp itself. This is the closest to automatic that is possible without the Meta
 *    credentials, and it disappears on its own once those are configured.
 *
 * SMS has no client-side equivalent: `sms:` links do nothing on desktop, and a browser cannot send
 * one. Automatic SMS comes from the Android gateway on the server, which is configured alongside
 * WhatsApp.
 */
export function autoSendAccountDetails(contact: PartyContact, alreadySentByServer: boolean): void {
  if (alreadySentByServer) return;

  const phone = normalizePhone(contact.phone);
  if (!phone) return; // No usable number — opening a blank chat helps nobody.

  const url = whatsAppUrl(phone, accountOpenedMessage(contact));

  // Opened in a new tab so the credentials dialog behind it stays readable. Popup blockers can
  // still refuse this, which is why the manual share button remains on the detail page.
  window.open(url, '_blank', 'noopener');
}
